import { RiskChip } from "./RiskChip";
import { LogoMark } from "./Logo";
import { buildTemplateContent } from "@/lib/templateContent";
import { TEMPLATES } from "@/lib/library";

const sample = buildTemplateContent(TEMPLATES.find((t) => t.id === "wah-scaffold-erect")!);

/** Browser chrome wrapper — makes a mockup read as a real product screenshot. */
function BrowserFrame({ url, children, className = "" }: { url: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`overflow-hidden rounded-2xl bg-white shadow-float ring-1 ring-black/5 ${className}`}>
      <div className="flex items-center gap-3 border-b border-line bg-mist px-4 py-2.5">
        <div className="flex gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-line-2" />
          <span className="h-2.5 w-2.5 rounded-full bg-line-2" />
          <span className="h-2.5 w-2.5 rounded-full bg-line-2" />
        </div>
        <div className="flex flex-1 items-center gap-1.5 rounded-md bg-white px-3 py-1 text-[0.68rem] text-muted ring-1 ring-line">
          <svg viewBox="0 0 20 20" className="h-3 w-3 text-emerald" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="9" width="12" height="8" rx="1.5" /><path d="M7 9V6.5a3 3 0 0 1 6 0V9" /></svg>
          {url}
        </div>
      </div>
      {children}
    </div>
  );
}

const NAV_SECTIONS = [
  { label: "Job details", done: true },
  { label: "Task & method", done: true },
  { label: "Hazards & controls", active: true },
  { label: "PPE & access", done: true },
  { label: "Review", done: false },
  { label: "Export", done: false },
];

// Plumbing & Heating example for the hero — both current leads are plumbing/heating.
const PLUMB_HAZARDS = [
  { hazard: "Soldering / hot works near combustibles", initial: { likelihood: 4, severity: 4 }, residual: { likelihood: 1, severity: 4 } },
  { hazard: "Manual handling of pipework & boilers", initial: { likelihood: 3, severity: 3 }, residual: { likelihood: 1, severity: 3 } },
  { hazard: "Hot pipework — burns & scalding", initial: { likelihood: 3, severity: 3 }, residual: { likelihood: 1, severity: 2 } },
  { hazard: "Work in an occupied plant room", initial: { likelihood: 3, severity: 2 }, residual: { likelihood: 1, severity: 2 } },
];

/** The RamsDocs app — editing a Plumbing & Heating RAMS. Hero / primary product shot. */
export function AppMockup({ className = "" }: { className?: string }) {
  return (
    <BrowserFrame url="app.ramsdocs.co.uk/r/plumbing-heating" className={className}>
      <div className="grid grid-cols-[132px_1fr] sm:grid-cols-[160px_1fr]">
        {/* sidebar */}
        <div className="border-r border-line bg-ink px-3 py-4">
          <div className="flex items-center gap-1.5">
            <LogoMark className="h-6 w-6" />
            <span className="font-display text-sm font-bold text-white">RamsDocs</span>
          </div>
          <div className="mt-5 space-y-1">
            {NAV_SECTIONS.map((s) => (
              <div key={s.label} className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-[0.7rem] ${s.active ? "bg-white/10 font-semibold text-white" : "text-white/55"}`}>
                <span className={`flex h-3.5 w-3.5 items-center justify-center rounded-full ${s.done ? "bg-emerald text-white" : s.active ? "bg-emerald/30 text-emerald" : "bg-white/15"}`}>
                  {s.done && <svg viewBox="0 0 20 20" className="h-2.5 w-2.5" fill="none" stroke="currentColor" strokeWidth="3"><path d="m4 10 4 4 8-9" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                </span>
                {s.label}
              </div>
            ))}
          </div>
        </div>
        {/* main */}
        <div className="px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[0.6rem] font-semibold uppercase tracking-wider text-emerald-deep">Plumbing &amp; Heating · Method statement</p>
              <p className="mt-0.5 font-display text-base font-bold text-ink">Pipework &amp; Soldering RAMS</p>
            </div>
            <span className="hidden items-center gap-1.5 rounded-full bg-emerald-tint px-2.5 py-1 text-[0.66rem] font-semibold text-emerald-deep sm:inline-flex">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald" /> In review
            </span>
          </div>
          <p className="mt-1 text-[0.7rem] text-muted">Plant room, Unit 4 · Brookfield Construction Ltd</p>

          <div className="mt-3 rounded-lg bg-mist px-3 py-2">
            <div className="flex items-center justify-between text-[0.66rem] font-medium text-ink">
              <span>Review checklist</span><span className="text-emerald-deep">7/9 checks</span>
            </div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-line">
              <div className="h-full rounded-full bg-emerald" style={{ width: "78%" }} />
            </div>
          </div>

          <div className="mt-3 grid grid-cols-[1fr_auto_auto] gap-x-3 border-b border-line pb-1.5 text-[0.6rem] font-semibold uppercase tracking-wider text-muted">
            <span>Hazard</span><span className="text-center">Initial</span><span className="text-center">Residual</span>
          </div>
          {PLUMB_HAZARDS.map((h) => (
            <div key={h.hazard} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-3 border-b border-line py-2 text-[0.78rem]">
              <span className="truncate leading-snug text-body">{h.hazard}</span>
              <span className="flex justify-center"><RiskChip rating={h.initial} /></span>
              <span className="flex justify-center"><RiskChip rating={h.residual} /></span>
            </div>
          ))}
          <div className="mt-3 flex items-center justify-between">
            <span className="text-[0.68rem] text-muted">Risk 1–25 · likelihood × severity</span>
            <span className="rounded-md bg-emerald px-3 py-1.5 text-[0.7rem] font-semibold text-white">Export PDF</span>
          </div>
        </div>
      </div>
    </BrowserFrame>
  );
}

/** The output document — a clean RAMS, represents the exported PDF. */
export function DocMockup({ className = "" }: { className?: string }) {
  return (
    <div className={`overflow-hidden rounded-2xl bg-white shadow-float ring-1 ring-black/5 ${className}`}>
      <div className="h-1.5 bg-emerald" />
      <div className="px-6 py-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><LogoMark className="h-7 w-7" /><span className="font-display text-sm font-bold text-ink">RamsDocs</span></div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-tint px-2.5 py-1 text-[0.66rem] font-semibold text-emerald-deep"><span className="h-1.5 w-1.5 rounded-full bg-emerald" /> Ready for review</span>
        </div>
        <p className="mt-4 text-[0.6rem] font-semibold uppercase tracking-wider text-muted">Method Statement &amp; Risk Assessment</p>
        <p className="mt-0.5 font-display text-lg font-bold text-ink">Scaffold Erection &amp; Dismantling</p>
        <p className="mt-0.5 text-xs text-muted">Site: Unit 4, Brookfield Ind. Est. · CDM 2015</p>
        <div className="mt-4 grid grid-cols-[1fr_auto_auto] gap-x-3 border-b border-line pb-1.5 text-[0.6rem] font-semibold uppercase tracking-wider text-muted">
          <span>Hazard</span><span className="text-center">Initial</span><span className="text-center">Residual</span>
        </div>
        {sample.hazards.slice(0, 4).map((h) => (
          <div key={h.hazard} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-3 border-b border-line py-2 text-[0.8rem]">
            <span className="leading-snug text-body">{h.hazard}</span>
            <span className="flex justify-center"><RiskChip rating={h.initial} /></span>
            <span className="flex justify-center"><RiskChip rating={h.residual} /></span>
          </div>
        ))}
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[0.7rem] text-muted">Risk 1–25 · likelihood × severity</span>
          <span className="rounded-md bg-ink px-3 py-1.5 text-[0.7rem] font-semibold text-white">Export PDF</span>
        </div>
      </div>
    </div>
  );
}

/** A slice of the template library, in browser chrome. */
export function LibraryMockup({ className = "" }: { className?: string }) {
  const items = ["wah-mewp", "elec-installation", "ground-excavation", "hot-welding", "coshh-silica", "demo-soft-strip"]
    .map((id) => TEMPLATES.find((t) => t.id === id)!)
    .filter(Boolean);
  return (
    <BrowserFrame url="ramsdocs.co.uk/templates" className={className}>
      <div className="p-5">
        <div className="flex items-center gap-2 rounded-lg border border-line bg-mist px-3 py-2 text-sm text-muted">
          <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="9" cy="9" r="6" /><path d="m17 17-3.5-3.5" strokeLinecap="round" /></svg>
          Search {TEMPLATES.length} RAMS templates…
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2.5">
          {items.map((t) => (
            <div key={t.id} className="rounded-lg border border-line p-3 transition-colors hover:border-emerald/50">
              <p className="text-[0.58rem] font-semibold uppercase tracking-wider text-emerald-deep">RAMS</p>
              <p className="mt-1 line-clamp-2 text-[0.78rem] font-semibold leading-snug text-ink">{t.title}</p>
            </div>
          ))}
        </div>
      </div>
    </BrowserFrame>
  );
}

/** The site-specific fill panel — the activation loop. */
export function FillMockup({ className = "" }: { className?: string }) {
  const fields = [
    { label: "Site address", value: "Unit 4, Brookfield Ind. Est." },
    { label: "Client / principal contractor", value: "Brookfield Construction Ltd" },
    { label: "Start date", value: "14 Jul 2026" },
    { label: "Site supervisor / competent person", value: "J. Whelan (CISRS)" },
  ];
  return (
    <BrowserFrame url="app.ramsdocs.co.uk/r/new" className={className}>
      <div className="p-5">
        <p className="text-[0.6rem] font-semibold uppercase tracking-wider text-emerald-deep">Make it site-specific</p>
        <div className="mt-3 space-y-2.5">
          {fields.map((f) => (
            <div key={f.label}>
              <p className="text-[0.68rem] font-medium text-muted">{f.label}</p>
              <div className="mt-1 flex items-center justify-between rounded-lg border border-line bg-mist px-3 py-2 text-[0.82rem] text-ink">
                {f.value}
                <svg viewBox="0 0 20 20" className="h-3.5 w-3.5 text-emerald" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="m4 10 4 4 8-9" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-2 rounded-lg bg-emerald-tint px-3 py-2.5 text-[0.78rem] font-medium text-emerald-deep">
          <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="m4 10 4 4 8-9" strokeLinecap="round" strokeLinejoin="round" /></svg>
          Document updated for this site
        </div>
      </div>
    </BrowserFrame>
  );
}
